import React, { useState, useEffect } from 'react';
import { Bot, Save, Plus, Trash2, Edit2 } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';

interface Pergunta {
  ordem: number;
  pergunta: string;
  tipo: 'texto' | 'sim_nao' | 'multipla_escolha';
  opcoes?: string[];
}

interface ConfiguracaoIAData {
  id?: string;
  nome_assistente: string;
  instrucoes_gerais: string;
  perguntas_qualificacao: Pergunta[];
  mensagem_inicial: string;
  mensagem_finalizacao: string;
  ativo: boolean;
}

const ConfiguracaoIA: React.FC = () => {
  const [config, setConfig] = useState<ConfiguracaoIAData>({
    nome_assistente: 'Sofia',
    instrucoes_gerais: 'Você é uma assistente virtual de RH da Fox Vendas. Seja educada, profissional e ajude os candidatos durante o processo seletivo.',
    perguntas_qualificacao: [
      { ordem: 1, pergunta: 'Qual é o seu nome?', tipo: 'texto' },
      { ordem: 2, pergunta: 'Você tem experiência anterior em vendas?', tipo: 'sim_nao' },
    ],
    mensagem_inicial: 'Olá! 👋 Sou a {nome_assistente}, assistente virtual da Fox Vendas.',
    mensagem_finalizacao: 'Perfeito! 🎉 Vou encaminhar seus dados para nossa equipe de RH.',
    ativo: true
  });
  const [loading, setLoading] = useState(false);
  const [editandoPergunta, setEditandoPergunta] = useState<number | null>(null);

  useEffect(() => {
    carregarConfiguracao();
  }, []);

  const carregarConfiguracao = async () => {
    try {
      const { data, error } = await supabase
        .from('configuracoes_ia_rh')
        .select('*')
        .eq('ativo', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar configuração:', error);
        return;
      }

      if (data) {
        setConfig({
          id: data.id,
          nome_assistente: data.nome_assistente,
          instrucoes_gerais: data.instrucoes_gerais,
          perguntas_qualificacao: Array.isArray(data.perguntas_qualificacao) ? data.perguntas_qualificacao as unknown as Pergunta[] : [],
          mensagem_inicial: data.mensagem_inicial,
          mensagem_finalizacao: data.mensagem_finalizacao,
          ativo: data.ativo
        });
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  const salvarConfiguracao = async () => {
    setLoading(true);
    try {
      const configData = {
        nome_assistente: config.nome_assistente,
        instrucoes_gerais: config.instrucoes_gerais,
        perguntas_qualificacao: config.perguntas_qualificacao as any,
        mensagem_inicial: config.mensagem_inicial,
        mensagem_finalizacao: config.mensagem_finalizacao,
        ativo: config.ativo
      };

      if (config.id) {
        const { error } = await supabase
          .from('configuracoes_ia_rh')
          .update(configData)
          .eq('id', config.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('configuracoes_ia_rh')
          .insert(configData);

        if (error) throw error;
      }

      alert('Configuração salva com sucesso!');
      carregarConfiguracao();
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      alert('Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const adicionarPergunta = () => {
    const novaPergunta: Pergunta = {
      ordem: config.perguntas_qualificacao.length + 1,
      pergunta: '',
      tipo: 'texto'
    };
    setConfig({
      ...config,
      perguntas_qualificacao: [...config.perguntas_qualificacao, novaPergunta]
    });
    setEditandoPergunta(config.perguntas_qualificacao.length);
  };

  const removerPergunta = (index: number) => {
    const novasPerguntas = config.perguntas_qualificacao.filter((_, i) => i !== index);
    // Reordenar as perguntas
    const perguntasReordenadas = novasPerguntas.map((p, i) => ({ ...p, ordem: i + 1 }));
    setConfig({
      ...config,
      perguntas_qualificacao: perguntasReordenadas
    });
  };

  const atualizarPergunta = (index: number, perguntaAtualizada: Pergunta) => {
    const novasPerguntas = [...config.perguntas_qualificacao];
    novasPerguntas[index] = perguntaAtualizada;
    setConfig({
      ...config,
      perguntas_qualificacao: novasPerguntas
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Bot className="w-8 h-8 text-orange-500 mr-3" />
            Configuração da IA
          </h1>
          <p className="text-gray-600">
            Configure o comportamento e perguntas da assistente virtual
          </p>
        </div>
        <button
          onClick={salvarConfiguracao}
          disabled={loading}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{loading ? 'Salvando...' : 'Salvar'}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configurações Básicas */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Configurações Básicas
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Assistente
              </label>
              <input
                type="text"
                value={config.nome_assistente}
                onChange={(e) => setConfig({ ...config, nome_assistente: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Ex: Sofia"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instruções Gerais
              </label>
              <textarea
                value={config.instrucoes_gerais}
                onChange={(e) => setConfig({ ...config, instrucoes_gerais: e.target.value })}
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Descreva como a IA deve se comportar..."
              />
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.ativo}
                  onChange={(e) => setConfig({ ...config, ativo: e.target.checked })}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">Assistente ativa</span>
              </label>
            </div>
          </div>
        </div>

        {/* Mensagens */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Mensagens Padrão
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem Inicial
              </label>
              <textarea
                value={config.mensagem_inicial}
                onChange={(e) => setConfig({ ...config, mensagem_inicial: e.target.value })}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Mensagem de boas-vindas..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Use {'{nome_assistente}'} e {'{vaga_titulo}'} como variáveis
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem de Finalização
              </label>
              <textarea
                value={config.mensagem_finalizacao}
                onChange={(e) => setConfig({ ...config, mensagem_finalizacao: e.target.value })}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Mensagem final do processo..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Perguntas de Qualificação */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Perguntas de Qualificação
          </h2>
          <button
            onClick={adicionarPergunta}
            className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Adicionar Pergunta</span>
          </button>
        </div>

        <div className="space-y-4">
          {config.perguntas_qualificacao.map((pergunta, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              {editandoPergunta === index ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pergunta {pergunta.ordem}
                    </label>
                    <input
                      type="text"
                      value={pergunta.pergunta}
                      onChange={(e) => atualizarPergunta(index, { ...pergunta, pergunta: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Digite a pergunta..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Resposta
                    </label>
                    <select
                      value={pergunta.tipo}
                      onChange={(e) => atualizarPergunta(index, { 
                        ...pergunta, 
                        tipo: e.target.value as 'texto' | 'sim_nao' | 'multipla_escolha'
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="texto">Texto Livre</option>
                      <option value="sim_nao">Sim/Não</option>
                      <option value="multipla_escolha">Múltipla Escolha</option>
                    </select>
                  </div>

                  {pergunta.tipo === 'multipla_escolha' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Opções (separadas por vírgula)
                      </label>
                      <input
                        type="text"
                        value={pergunta.opcoes?.join(', ') || ''}
                        onChange={(e) => atualizarPergunta(index, { 
                          ...pergunta, 
                          opcoes: e.target.value.split(',').map(op => op.trim()).filter(op => op)
                        })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Opção 1, Opção 2, Opção 3..."
                      />
                    </div>
                  )}

                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditandoPergunta(null)}
                      className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Salvar
                    </button>
                    <button
                      onClick={() => setEditandoPergunta(null)}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {pergunta.ordem}. {pergunta.pergunta}
                    </p>
                    <p className="text-sm text-gray-500">
                      Tipo: {pergunta.tipo === 'texto' ? 'Texto Livre' : 
                             pergunta.tipo === 'sim_nao' ? 'Sim/Não' : 'Múltipla Escolha'}
                      {pergunta.opcoes && ` (${pergunta.opcoes.join(', ')})`}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditandoPergunta(index)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => removerPergunta(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConfiguracaoIA;